"use client";

import { Fragment } from "react";
import { useTranslations } from "next-intl";

const NAMESPACE = "new-mjf-multi-jet-fusion";

const SIZE_COLS = [
	{ key: "Under30", line1: "productionTolerancesColUnder30Line1", line2: "productionTolerancesColUnder30Line2" },
	{ key: "30to50", line1: "productionTolerancesCol30to50Line1", line2: "productionTolerancesCol30to50Line2" },
	{ key: "50to80", line1: "productionTolerancesCol50to80Line1", line2: "productionTolerancesCol50to80Line2" },
	{ key: "Over80", line1: "productionTolerancesColOver80Line1", line2: "productionTolerancesColOver80Line2" },
] as const;

const ROWS = [
	{
		materialKey: "productionTolerancesRow1Material",
		cells: [
			{ xy: "productionTolerancesRow1Under30XY", z: "productionTolerancesRow1Under30Z" },
			{ xy: "productionTolerancesRow1Col30to50XY", z: "productionTolerancesRow1Col30to50Z" },
			{ xy: "productionTolerancesRow1Col50to80XY", z: "productionTolerancesRow1Col50to80Z" },
			{ xy: "productionTolerancesRow1ColOver80XY", z: "productionTolerancesRow1ColOver80Z" },
		],
	},
	{
		materialKey: "productionTolerancesRow2Material",
		cells: [
			{ xy: "productionTolerancesRow2Under30XY", z: "productionTolerancesRow2Under30Z" },
			{ xy: "productionTolerancesRow2Col30to50XY", z: "productionTolerancesRow2Col30to50Z" },
			{ xy: "productionTolerancesRow2Col50to80XY", z: "productionTolerancesRow2Col50to80Z" },
			{ xy: "productionTolerancesRow2ColOver80XY", z: "productionTolerancesRow2ColOver80Z" },
		],
	},
] as const;

export default function MjfProductionTolerances() {
	const t = useTranslations(NAMESPACE);

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-3 text-center text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
					{t("productionTolerancesTitle")}
				</h2>
				<p className="mx-auto mb-6 text-center text-sm leading-relaxed text-black sm:mb-8 md:mb-10 md:text-base lg:mb-12">
					{t("productionTolerancesDesc")}
				</p>

				<div className="overflow-x-auto border border-[#d5d9cc] bg-[#fafcef]">
					<table className="w-full min-w-[760px] border-collapse text-left">
						<thead>
							<tr className="bg-themePrimary400 text-white">
								<th className="border-r border-[#d5d9cc] px-4 py-4 text-sm font-semibold sm:px-6 sm:py-5 md:text-base">
									{t("productionTolerancesColMaterial")}
								</th>
								{SIZE_COLS.map((col, idx) => (
									<th
										key={col.key}
										className={`px-4 py-4 text-sm font-semibold sm:px-6 sm:py-5 md:text-base${
											idx < SIZE_COLS.length - 1 ? " border-r border-[#d5d9cc]" : ""
										}`}
									>
										<span className="block">{t(col.line1)}</span>
										<span className="block">{t(col.line2)}</span>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{ROWS.map((row) => (
								<Fragment key={row.materialKey}>
									<tr className="border-t border-[#d5d9cc]">
										<th
											scope="row"
											rowSpan={2}
											className="border-r border-[#d5d9cc] px-4 py-4 align-middle text-sm font-bold text-black sm:px-6 sm:py-5 md:text-base"
										>
											{t(row.materialKey)}
										</th>
										{row.cells.map((cell, idx) => (
											<td
												key={idx}
												className={`px-4 py-3 text-sm leading-relaxed text-black sm:px-6 sm:py-4 md:text-base${
													idx < row.cells.length - 1 ? " border-r border-[#d5d9cc]" : ""
												}`}
											>
												{t(cell.xy)}
											</td>
										))}
									</tr>
									<tr className="border-t border-[#d5d9cc]">
										{row.cells.map((cell, idx) => (
											<td
												key={idx}
												className={`px-4 py-3 text-sm leading-relaxed text-black sm:px-6 sm:py-4 md:text-base${
													idx < row.cells.length - 1 ? " border-r border-[#d5d9cc]" : ""
												}`}
											>
												{t(cell.z)}
											</td>
										))}
									</tr>
								</Fragment>
							))}
						</tbody>
					</table>
				</div>

				<p className="mt-5 text-sm leading-relaxed text-black sm:mt-6 md:text-base">
					<span className="font-bold">{t("productionTolerancesNoteLabel")}</span>
					{t("productionTolerancesNoteText")}
				</p>
			</div>
		</section>
	);
}
